import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchPatientAnalyses,
    triggerPatientAnalysis,
    type PatientAnalysis,
    type TriggerAnalysisResponse
} from '../services/analysis';
import { PATIENT_ANALYSIS_KEY } from './queryKeys';

// The generation job usually takes ~30s; poll the list while one is running so
// the tab flips to success/error on its own ("fire and forget" on the server).
const PENDING_POLL_MS = 5 * 1000;
const IDLE_STALE_MS = 60 * 1000;

function hasPendingRun(analyses: PatientAnalysis[] | undefined): boolean {
    return (analyses ?? []).some((a) => a.status === 'pending');
}

export function usePatientAnalyses(patientId: string | undefined) {
    const queryClient = useQueryClient();

    const { data, error, isLoading } = useQuery<PatientAnalysis[]>({
        queryKey: PATIENT_ANALYSIS_KEY(patientId ?? ''),
        queryFn: () => fetchPatientAnalyses(patientId!),
        enabled: !!patientId,
        staleTime: IDLE_STALE_MS,
        refetchInterval: (query) => (hasPendingRun(query.state.data) ? PENDING_POLL_MS : false)
    });

    const refetch = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: PATIENT_ANALYSIS_KEY(patientId ?? '') });
    }, [queryClient, patientId]);

    const analyses = data ?? [];
    return {
        analyses,
        latest: analyses[0] ?? null,
        isLoading,
        error,
        refetch
    };
}

export function useTriggerAnalysis(patientId: string | undefined) {
    const queryClient = useQueryClient();

    return useMutation<TriggerAnalysisResponse, unknown, void>({
        mutationFn: () => triggerPatientAnalysis(patientId!),
        onSuccess: () => {
            // Pick up the new pending row right away so polling kicks in.
            queryClient.invalidateQueries({ queryKey: PATIENT_ANALYSIS_KEY(patientId ?? '') });
        }
    });
}
